// DETECTAR SOLO DÓNDE VA LA FIRMA EN UN PDF (Lissette 2026-10-23).
//
// Lee el TEXTO del PDF con sus posiciones (pdf.js, en el navegador: el
// documento no sale del equipo de la agente para esto) y propone cajas donde
// ve una etiqueta de firma con su raya:
//
//   "Signature: ______________"      → caja SOBRE la raya de la derecha
//   "______________\nFirma"          → caja SOBRE la raya de encima
//   "______ Initials"                → caja sobre la raya pegada a la etiqueta
//   "Printed name: ______", "Nombre y apellido", "Date: ____" junto a una firma
//
// Es una PROPUESTA: la agente la revisa en el editor (mueve, borra, añade) y la
// guarda. Nunca se envía nada sin que ella lo haya visto.
//
// Límites, a propósito y escritos para que nadie los descubra en un cliente:
//   - Un PDF ESCANEADO no tiene texto: no se detecta nada (se colocan a mano).
//   - Las rayas DIBUJADAS como línea (no con guiones bajos) no son texto: se
//     adivina la caja a la derecha de la etiqueta si hay hueco, o encima.
//   - "Fecha"/"Date" y "Nombre"/"Name" a secas solo se proponen cerca de una
//     firma: sueltos son casi siempre otra cosa (fecha de nacimiento, el nombre
//     del asegurado ya escrito…).
//   - Las firmas del AGENTE, testigo, notario o empleador se saltan: el que
//     firma aquí es el cliente.
//
// Funciones PURAS (sin pdf.js ni DOM): se prueban en deskte-api/tests/unit con
// textos de mentira.

import { limitarCaja, seSolapan, MAX_CAMPOS_PDF } from './camposSobreElPdf.js';

/** Multiplica dos matrices de transformación de PDF (como `pdfjsLib.Util.transform`). */
export function multiplicarMatrices(m1, m2) {
  return [
    m1[0] * m2[0] + m1[2] * m2[1],
    m1[1] * m2[0] + m1[3] * m2[1],
    m1[0] * m2[2] + m1[2] * m2[3],
    m1[1] * m2[2] + m1[3] * m2[3],
    m1[0] * m2[4] + m1[2] * m2[5] + m1[4],
    m1[1] * m2[4] + m1[3] * m2[5] + m1[5],
  ];
}

/**
 * Los trozos de texto de una página de pdf.js (`getTextContent().items`) en
 * coordenadas de la página TAL COMO SE VE: x/y de la esquina de arriba a la
 * izquierda, en puntos si el viewport es de escala 1.
 */
export function itemsDeTextoDePdfjs(items, transformDelViewport) {
  const vt = Array.isArray(transformDelViewport) ? transformDelViewport : [1, 0, 0, -1, 0, 792];
  const escala = Math.hypot(vt[0], vt[1]) || 1;
  const out = [];
  for (const it of Array.isArray(items) ? items : []) {
    if (!it || typeof it.str !== 'string' || !it.str.trim() || !Array.isArray(it.transform)) continue;
    const tx = multiplicarMatrices(vt, it.transform);
    const alto = Math.hypot(tx[2], tx[3]) || Math.abs(Number(it.height) || 0) * escala || 10;
    out.push({ texto: it.str, x: tx[4], y: tx[5] - alto, ancho: (Number(it.width) || 0) * escala, alto });
  }
  return out;
}

const sinAcentos = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Las etiquetas, de la más concreta a la más suelta. `cerca`: solo vale junto a
// una firma de la misma zona.
const ETIQUETAS = [
  // "Signature date" es una FECHA, no una firma: va primero para quedarse la palabra.
  { tipo: 'fecha', re: /\b(signature date|date signed|fecha de (la )?firma)\b/g },
  { tipo: 'iniciales', re: /\b(iniciales?|initials?|initial here)\b/g },
  { tipo: 'firma', re: /\b(firma|firme aqui|signature|sign here)\b/g },
  { tipo: 'nombre', re: /\b(printed name|print name|full name|nombre completo|nombre y apellidos?|nombre en letra de molde|nombre impreso|name \(print(ed)?\)|applicant'?s name|insured'?s name)\b/g },
  { tipo: 'nombre', re: /\b(name|nombre)\b/g, cerca: true },
  { tipo: 'fecha', re: /\b(fecha|date)\b/g, cerca: true },
];
// Quien firma aquí es el cliente.
const DE_OTRO = /\b(agent|agente|producer|productor|broker|witness|testigo|notary|notario|representative|representante|employer|empleador|spouse'?s? signature)\b/;
// "Fecha" que NO es la de la firma.
const OTRA_FECHA = /\b(birth|nacimiento|effective|efectiva|vigencia|expiration|vencimiento|issue|emision|hire|inicio|start|termination)\b/;
// Una raya de guiones bajos (3 o más).
const RAYA = /_{3,}/g;

/** Alto, ancho mínimo y máximo de la caja de cada tipo, en puntos. */
const MEDIDAS = {
  firma: { h: 26, min: 100, max: 260 },
  iniciales: { h: 20, min: 30, max: 90 },
  nombre: { h: 16, min: 100, max: 280 },
  fecha: { h: 16, min: 55, max: 130 },
};

/** ¿Parece una etiqueta de formulario (y no una frase de un párrafo)? */
function pareceEtiqueta(texto, inicio, fin) {
  const limpio = texto.replace(RAYA, ' ').replace(/\s+/g, ' ').trim();
  if (limpio.length <= 60) return true;
  // En una línea larga, solo si a la etiqueta la sigue (o la precede) una raya o dos puntos.
  return /^\s*[:.]?\s*_{3,}/.test(texto.slice(fin)) || /_{3,}\s*\(?\s*$/.test(texto.slice(0, inicio));
}

/**
 * Ancho APROXIMADO de una letra, en milésimas de su tamaño (las de Helvetica,
 * que es lo que usan casi todos los formularios). pdf.js solo da el ancho del
 * trozo entero; repartirlo a partes iguales entre las letras desplaza la raya
 * de "Applicant Signature: ____" varios puntos, porque una "i" mide la mitad
 * que una "_".
 */
function anchoDeLetra(ch) {
  if (ch === '_') return 556;
  if (/[ il.,:;'!|jtfI\[\]()]/.test(ch)) return 278;
  if (/[mwMW]/.test(ch)) return 833;
  if (/[A-Z]/.test(ch)) return 667;
  return 540;
}

/** Dónde caen (en puntos) los caracteres [desde, hasta) de un trozo de texto. */
function tramoEnX(item, desde, hasta) {
  const letras = Array.from(item.texto);
  let total = 0, antes = 0, hastaFin = 0;
  letras.forEach((ch, i) => {
    const a = anchoDeLetra(ch);
    if (i < desde) antes += a;
    if (i < hasta) hastaFin += a;
    total += a;
  });
  const escala = total ? item.ancho / total : 0;
  return { x0: item.x + antes * escala, x1: item.x + hastaFin * escala };
}

const lineaBase = (it) => it.y + it.alto;
const mismaLinea = (a, b) => Math.abs(lineaBase(a) - lineaBase(b)) <= Math.max(3, 0.45 * Math.min(a.alto, b.alto));

/** Las rayas de guiones bajos de un trozo, en puntos. */
function rayasDe(item) {
  const out = [];
  const re = new RegExp(RAYA.source, 'g');
  let m = re.exec(item.texto);
  while (m) {
    out.push({ ...tramoEnX(item, m.index, m.index + m[0].length), base: lineaBase(item), desde: m.index, hasta: m.index + m[0].length });
    m = re.exec(item.texto);
  }
  return out;
}

/** Todas las etiquetas de un trozo, en orden, ya filtradas. */
function etiquetasDe(item) {
  const texto = sinAcentos(item.texto);
  const halladas = [];
  for (const e of ETIQUETAS) {
    const re = new RegExp(e.re.source, 'g');
    let m = re.exec(texto);
    while (m) {
      const inicio = m.index;
      const fin = inicio + m[0].length;
      // Una palabra ya cogida por una etiqueta más concreta no cuenta dos veces.
      if (!halladas.some((h) => inicio < h.fin && fin > h.inicio) && pareceEtiqueta(texto, inicio, fin)) {
        const alrededor = texto.slice(Math.max(0, inicio - 30), Math.min(texto.length, fin + 12));
        const descartada = (e.tipo !== 'fecha' && DE_OTRO.test(alrededor))
          || (e.tipo === 'fecha' && (OTRA_FECHA.test(alrededor) || DE_OTRO.test(alrededor)));
        if (!descartada) halladas.push({ tipo: e.tipo, cerca: !!e.cerca, inicio, fin });
      }
      m = re.exec(texto);
    }
  }
  return halladas.sort((a, b) => a.inicio - b.inicio);
}

/**
 * La raya que le toca a una etiqueta. Por orden: en el mismo trozo detrás de
 * ella; en el mismo trozo justo delante; en la misma línea a la derecha; en la
 * misma línea pegada a la izquierda; en la línea de ENCIMA (la etiqueta debajo
 * de la raya).
 */
function rayaDeLaEtiqueta(item, et, siguiente, anterior, items) {
  const propias = rayasDe(item);
  const detras = propias.find((r) => r.desde >= et.fin && (!siguiente || r.desde < siguiente.inicio));
  if (detras) return detras;
  const delante = [...propias].reverse().find((r) => r.hasta <= et.inicio && et.inicio - r.hasta <= 6 && (!anterior || r.desde >= anterior.fin));
  if (delante) return delante;

  const { x0, x1 } = tramoEnX(item, et.inicio, et.fin);
  const enLinea = items.filter((o) => o !== item && mismaLinea(o, item));
  // A la derecha: el PRIMER trozo de la línea; si no es una raya, no hay raya.
  const derecha = enLinea.filter((o) => o.x >= x1 - 2 && o.x - x1 <= 320).sort((a, b) => a.x - b.x)[0];
  if (derecha && !siguiente) {
    const r = rayasDe(derecha)[0];
    if (r && r.desde <= 2) return r;
  }
  const izquierda = enLinea.filter((o) => o.x + o.ancho <= x0 + 2 && x0 - (o.x + o.ancho) <= 40).sort((a, b) => b.x - a.x)[0];
  if (izquierda && !anterior) {
    const r = rayasDe(izquierda).pop();
    if (r && r.hasta >= izquierda.texto.trimEnd().length - 1) return r;
  }
  // Encima: una raya cuya línea base queda justo por encima de la etiqueta y
  // que cae sobre ella a lo ancho.
  const arriba = items
    .filter((o) => o !== item && lineaBase(o) <= item.y + 2 && item.y - lineaBase(o) <= 28)
    .flatMap((o) => rayasDe(o))
    .filter((r) => r.x0 < x1 + 40 && r.x1 > x0 - 40)
    .sort((a, b) => b.base - a.base)[0];
  return arriba || null;
}

/** Sin raya: a la derecha si hay hueco en la línea; si no, encima (raya dibujada). */
function cajaSinRaya(item, et, items, anchoPagina, med) {
  const { x0, x1 } = tramoEnX(item, et.inicio, et.fin);
  // Lo que quede de este mismo trozo detrás de la etiqueta ocupa sitio.
  const restoPropio = item.texto.slice(et.fin).replace(/^[\s:.]+/, '');
  const finOcupado = restoPropio ? item.x + item.ancho : x1;
  const siguienteEnLinea = items
    .filter((o) => o !== item && mismaLinea(o, item) && o.x >= finOcupado - 2)
    .sort((a, b) => a.x - b.x)[0];
  const limite = siguienteEnLinea ? siguienteEnLinea.x : anchoPagina - 36;
  const hueco = limite - finOcupado - 8;
  if (!restoPropio && hueco >= med.min * 0.9) {
    return { x: x1 + 4, ancho: Math.min(hueco, med.max), base: lineaBase(item) };
  }
  return { x: x0, ancho: Math.max(med.min, Math.min(x1 - x0, med.max)), base: item.y - 1 };
}

/**
 * Propone sitios para un PDF entero.
 *
 * `paginas`: [{ page (1..), ancho, alto (puntos), items: itemsDeTextoDePdfjs(...) }]
 * Devuelve cajas en fracciones, listas para el editor, sin pasar de
 * `MAX_CAMPOS_PDF` ni repetir dos encima de la misma raya.
 */
export function detectarSitiosDeFirma(paginas) {
  const out = [];
  let n = 0;
  for (const pag of Array.isArray(paginas) ? paginas : []) {
    const ancho = Number(pag?.ancho) || 612;
    const alto = Number(pag?.alto) || 792;
    const items = (Array.isArray(pag?.items) ? pag.items : []).filter((it) => it && it.texto && it.ancho >= 0);
    const propuestas = [];
    for (const item of items) {
      const ets = etiquetasDe(item);
      ets.forEach((et, i) => {
        const raya = rayaDeLaEtiqueta(item, et, ets[i + 1], ets[i - 1], items);
        // Lo suelto ("Name", "Date") sin raya propia no se adivina.
        if (!raya && et.cerca) return;
        const med = MEDIDAS[et.tipo];
        const sitio = raya
          ? { x: raya.x0, ancho: Math.max(med.min, Math.min(raya.x1 - raya.x0, med.max)), base: raya.base + 1 }
          : cajaSinRaya(item, et, items, ancho, med);
        propuestas.push({ tipo: et.tipo, cerca: et.cerca, base: sitio.base, x: sitio.x, w: sitio.ancho, h: med.h });
      });
    }
    // "Fecha"/"Nombre" sueltos: solo junto a una firma (misma línea o ±60 pt).
    const firmas = propuestas.filter((p) => p.tipo === 'firma');
    const validas = propuestas.filter((p) => !p.cerca || firmas.some((f) => Math.abs(f.base - p.base) <= 60));
    for (const p of validas) {
      if (out.length >= MAX_CAMPOS_PDF) return out;
      const caja = limitarCaja({
        id: `auto_${pag.page}_${++n}`,
        tipo: p.tipo,
        page: pag.page,
        x: p.x / ancho,
        y: (p.base - p.h) / alto,
        w: p.w / ancho,
        h: p.h / alto,
      });
      if (!out.some((o) => seSolapan(o, caja))) out.push(caja);
    }
  }
  return out;
}
